import React, {
  useEffect,
  useState
} from 'react';

import { useNavigate } from 'react-router-dom';

import {
  FaSearch,
  FaBan,
  FaCheck,
  FaEye,
  FaFilter
} from 'react-icons/fa';

import {
  getUsers
} from '../services/userService';

import './Users.css';

const Users = () => {

  const navigate = useNavigate();

  /*
  |--------------------------------------------------------------------------
  | STATES
  |--------------------------------------------------------------------------
  */

  const [loading, setLoading] =
    useState(true);

  const [users, setUsers] =
    useState([]);

  const [searchTerm, setSearchTerm] =
    useState('');

  const [filterStatus, setFilterStatus] =
    useState('all');

  const [currentPage, setCurrentPage] =
    useState(1);

  const [totalPages, setTotalPages] =
    useState(1);

  const [totalUsers, setTotalUsers] =
    useState(0);

  /*
  |--------------------------------------------------------------------------
  | FETCH USERS
  |--------------------------------------------------------------------------
  */

  const fetchUsers = async () => {

    try {

      setLoading(true);

      const response = await getUsers(
        currentPage,
        searchTerm,
        filterStatus
      );

      console.log(
        "Users Response:",
        response
      );

      setUsers(
        response.data || []
      );

      setTotalPages(
        response.pagination?.last_page || 1
      );

      setTotalUsers(
        response.pagination?.total || 0
      );

    } catch (error) {

      console.log(
        "Users Error:",
        error
      );

    } finally {

      setLoading(false);

    }
  };

  /*
  |--------------------------------------------------------------------------
  | USE EFFECT
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    fetchUsers();

  }, [
    currentPage,
    filterStatus,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  /*
  |--------------------------------------------------------------------------
  | PAGINATION LOGIC
  |--------------------------------------------------------------------------
  */

  const renderPaginationButtons = () => {

    const buttons = [];

    // First Page
    buttons.push(1);

    // Left Dots
    if (currentPage > 4) {
      buttons.push('left-dots');
    }

    // Middle Pages
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      buttons.push(i);
    }

    // Right Dots
    if (currentPage < totalPages - 3) {
      buttons.push('right-dots');
    }

    // Last Page
    if (totalPages > 1) {
      buttons.push(totalPages);
    }

    return buttons.map((item, index) => {

      if (
        item === 'left-dots' ||
        item === 'right-dots'
      ) {

        return (

          <span
            key={index}
            className="pagination-dots"
          >

            ...

          </span>

        );
      }

      return (

        <button
          key={item}
          className={`pagination-btn ${currentPage === item
            ? 'active'
            : ''
            }`}
          onClick={() =>
            setCurrentPage(item)
          }
        >

          {item}

        </button>

      );
    });
  };

  /*
  |--------------------------------------------------------------------------
  | SEARCH
  |--------------------------------------------------------------------------
  */

  const handleSearch = () => {

    setCurrentPage(1);

    fetchUsers();
  };

  /*
  |--------------------------------------------------------------------------
  | ACTIONS
  |--------------------------------------------------------------------------
  */

  const handleAction = (
    action,
    userId
  ) => {

    if (action === 'view') {

      const user = users.find(
        u => u.id === userId
      );

      if (user) {

        navigate(
          `/users/${userId}`,
          {
            state: { user }
          }
        );
      }

    } else {

      console.log(
        `${action} user with ID: ${userId}`
      );
    }

  };

  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  if (loading) {

    return (

      <div className="dashboard-loading">

        Loading Users...

      </div>

    );
  }

  return (

    <div className="users-page">

      {/* HEADER */}

      <div className="page-header">

        <h1>
          User Management
        </h1>

        <p>
          Manage and monitor all
          registered users
        </p>

      </div>

      {/* CONTROLS */}

      <div className="users-controls">

        {/* SEARCH */}

        <div className="search-wrapper">

          <FaSearch className="search-icon" />

          <input
            type="text"
            placeholder="Search wallet..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => {

              setSearchTerm(
                e.target.value
              );

            }}
            onKeyDown={(e) => {

              if (e.key === 'Enter') {

                handleSearch();

              }

            }}
          />

          <button
            className="search-btn"
            onClick={handleSearch}
          >

            Search

          </button>

        </div>

        {/* FILTER */}

        <div className="filter-wrapper">

          <FaFilter className="filter-icon" />

          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => {

              setFilterStatus(
                e.target.value
              );

              setCurrentPage(1);

            }}
          >

            <option value="all">
              All Status
            </option>

            <option value="active">
              Active
            </option>

            <option value="pending">
              Pending
            </option>

            <option value="blocked">
              Blocked
            </option>

          </select>

        </div>

      </div>

      {/* TABLE */}

      <div className="users-table-container">

        <div className="table-responsive">

          <table className="users-table">

            <thead>

              <tr>

                <th>ID</th>

                <th>Name</th>

                <th>Wallet</th>

                <th>Sponsor</th>

                <th>Trade</th>

                <th>Total Income</th>

                <th>Available</th>

                <th>Status</th>

                <th>Joined</th>

                <th>Actions</th>

              </tr>

            </thead>

            <tbody>

              {users.length > 0 ? (

                users.map((user) => (

                  <tr key={user.id}>

                    {/* ID */}

                    <td>

                      #{user.id}

                    </td>

                    {/* NAME */}

                    <td>

                      <div className="user-info">

                        <span>

                          {user.name
                            ? user.name
                            : 'N/A'}

                        </span>

                      </div>

                    </td>

                    {/* WALLET */}

                    <td>

                      <div className="user-info">

                        <span>

                          {user.wallet
                            ? `${user.wallet.slice(
                              0,
                              5
                            )}...${user.wallet.slice(
                              -5
                            )}`
                            : 'N/A'}

                        </span>

                      </div>

                    </td>

                    {/* SPONSOR */}

                    <td>

                      {user.sponsor_wallet
                        ? `${user.sponsor_wallet.slice(
                          0,
                          5
                        )}...${user.sponsor_wallet.slice(
                          -5
                        )}`
                        : 'N/A'}

                    </td>

                    {/* DEPOSIT */}

                    <td className="balance">

                      $
                      {Number(
                        user.deposit || 0
                      ).toLocaleString()}

                    </td>

                    {/* TOTAL INCOME */}

                    <td className="balance">

                      $
                      {Number(
                        user.total_income || 0
                      ).toLocaleString()}

                    </td>

                    {/* AVAILABLE */}

                    <td className="balance">

                      $
                      {Number(
                        user.available_income || 0
                      ).toLocaleString()}

                    </td>

                    {/* STATUS */}

                    <td>

                      <span
                        className={`status-badge status-${(
                          user.status || ''
                        ).toLowerCase()}`}
                      >

                        {user.status}

                      </span>

                    </td>

                    {/* JOINED */}

                    <td>

                      {user.joined}

                    </td>

                    {/* ACTIONS */}

                    <td>

                      <div className="action-buttons">

                        {/* VIEW */}

                        <button
                          className="action-btn view"
                          onClick={() =>
                            handleAction(
                              'view',
                              user.id
                            )
                          }
                          title="View Profile"
                        >

                          <FaEye />

                        </button>

                        {/* BLOCK / ACTIVE */}

                        {user.status ===
                          'active' ? (

                          <button
                            className="action-btn block"
                            onClick={() =>
                              handleAction(
                                'block',
                                user.id
                              )
                            }
                            title="Block User"
                          >

                            <FaBan />

                          </button>

                        ) : (

                          <button
                            className="action-btn activate"
                            onClick={() =>
                              handleAction(
                                'activate',
                                user.id
                              )
                            }
                            title="Activate User"
                          >

                            <FaCheck />

                          </button>

                        )}

                      </div>

                    </td>

                  </tr>

                ))

              ) : (

                <tr>

                  <td
                    colSpan="10"
                    className="text-center"
                  >

                    No Users Found

                  </td>

                </tr>

              )}

            </tbody>

          </table>

        </div>

        {/* PAGINATION */}

        <div className="pagination-wrapper">

          <div className="pagination-info">

            Total Users:
            {' '}
            {totalUsers}

          </div>

          <div className="pagination">

            {/* PREVIOUS */}

            <button
              className="pagination-btn"
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.max(prev - 1, 1)
                )
              }
              disabled={currentPage === 1}
            >

              Previous

            </button>

            {/* PAGINATION BUTTONS */}

            {renderPaginationButtons()}

            {/* NEXT */}

            <button
              className="pagination-btn"
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.min(
                    prev + 1,
                    totalPages
                  )
                )
              }
              disabled={
                currentPage === totalPages
              }
            >

              Next

            </button>

          </div>

        </div>

      </div>

    </div>

  );
};

export default Users;